// tracking-format.util.ts — small pure formatters shared across the Tracking Demo UI.
import type { ClientSecurityInfo, TrackingSessionStatus } from './tracking-demo.models';

/**
 * Where the demo was launched from, for the "Source" column/row: "App · iOS",
 * "App · Android", or "Web". Reads the DECLARED platform (the entry link's
 * `?device_type=`), not the UA-sniffed device type — a desktop opening an app
 * link honestly shows "App · iOS" next to "desktop". Null security (no capture
 * yet) renders as an em dash like every other unknown.
 */
export function launchSourceLabel(sec: Pick<ClientSecurityInfo, 'appPlatform'> | null | undefined): string {
  if (!sec) return '—';
  switch ((sec.appPlatform || '').toLowerCase()) {
    case 'ios': return 'App · iOS';
    case 'android': return 'App · Android';
    default: return 'Web';
  }
}

/**
 * Parse an API timestamp. Backend datetimes are naive-UTC `isoformat()` strings (no zone
 * suffix), while client-stamped ones (e.g. a slide's `shownAt`) carry a real `Z` — a plain
 * `new Date(iso)` reads the zoneless ones as LOCAL time and shifts every backend timestamp
 * by the viewer's UTC offset. Zoneless strings are therefore pinned to UTC here.
 */
export function parseApiDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const zoneless = /T\d{2}:\d{2}/.test(iso) && !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(iso);
  const d = new Date(zoneless ? `${iso}Z` : iso);
  return isNaN(d.getTime()) ? null : d;
}

/** Epoch ms of an API timestamp (see {@link parseApiDate}), or null. */
export function parseApiMs(iso: string | null | undefined): number | null {
  const d = parseApiDate(iso);
  return d ? d.getTime() : null;
}

export function formatDuration(ms: number): string {
  if (!isFinite(ms) || ms < 0) return '—';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

export function durationSecToStr(sec: number | null | undefined): string {
  if (sec == null) return '—';
  return formatDuration(sec * 1000);
}

export function liveDuration(startedAt: string | null | undefined, now: number): string {
  const started = parseApiMs(startedAt);
  if (started == null) return '—';
  return formatDuration(now - started);
}

export function shortId(id: string | null | undefined, n = 8): string {
  if (!id) return '—';
  return id.length <= n ? id : `${id.slice(0, n)}…`;
}

export function timeAgo(iso: string | null | undefined, now: number): string {
  if (!iso) return '—';
  const diff = Math.max(0, now - (parseApiMs(iso) ?? now));
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const STATUS_CLASSES: Record<string, string> = {
  waiting: 'bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-400/10 dark:text-amber-300',
  connecting: 'bg-sky-50 text-sky-700 ring-sky-600/20 dark:bg-sky-400/10 dark:text-sky-300',
  live: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-400/10 dark:text-emerald-300',
  on_hold: 'bg-yellow-50 text-yellow-700 ring-yellow-600/20 dark:bg-yellow-400/10 dark:text-yellow-300',
  ended: 'bg-gray-100 text-gray-600 ring-gray-500/20 dark:bg-gray-400/10 dark:text-gray-300',
  disconnected_by_admin: 'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-400/10 dark:text-red-300',
  expired: 'bg-gray-100 text-gray-500 ring-gray-500/20 dark:bg-gray-500/10 dark:text-gray-400',
  blocked: 'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-400/10 dark:text-red-300',
  rejected: 'bg-orange-50 text-orange-700 ring-orange-600/20 dark:bg-orange-400/10 dark:text-orange-300',
};

export function statusClass(status: TrackingSessionStatus | string): string {
  return STATUS_CLASSES[status] || STATUS_CLASSES['ended'];
}

const STATUS_DOTS: Record<string, string> = {
  waiting: 'bg-amber-500',
  connecting: 'bg-sky-500 animate-pulse',
  live: 'bg-emerald-500 animate-pulse',
  on_hold: 'bg-yellow-500',
  ended: 'bg-gray-400',
  disconnected_by_admin: 'bg-red-500',
  expired: 'bg-gray-400',
  blocked: 'bg-red-500',
  rejected: 'bg-orange-500',
};

export function statusDot(status: TrackingSessionStatus | string): string {
  return STATUS_DOTS[status] || 'bg-gray-400';
}

export function statusLabel(status: string): string {
  return (status || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
